import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';

export interface UserPreferences {
  // Default renovation inputs
  defaultScope: string;
  defaultQualityLevel: string;
  defaultTimeline: string;
  defaultMepScope: string;
  defaultPermitComplexity: string;
  // UI preferences
  analysisPageSize: number;
  projectPageSize: number;
  defaultAnalysisSort: string;
  defaultProjectSort: string;
  // Report preferences
  autoOpenPrintDialog: boolean;
  defaultExportFormat: 'pdf' | 'xlsx';
  // Dashboard
  showDashboardCharts: boolean;
}

const DEFAULT_PREFS: UserPreferences = {
  defaultScope: 'full_gut',
  defaultQualityLevel: 'high_end',
  defaultTimeline: 'standard',
  defaultMepScope: 'partial',
  defaultPermitComplexity: 'medium',
  analysisPageSize: 15,
  projectPageSize: 15,
  defaultAnalysisSort: 'created_at',
  defaultProjectSort: 'created_at',
  autoOpenPrintDialog: true,
  defaultExportFormat: 'pdf',
  showDashboardCharts: true,
};

interface PreferencesContextType {
  prefs: UserPreferences;
  setPref: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
  resetPrefs: () => void;
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

function getStorageKey(uid: string | undefined): string {
  return `cca_prefs_${uid || 'anonymous'}`;
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<UserPreferences>(DEFAULT_PREFS);

  // Load prefs when user changes
  useEffect(() => {
    const key = getStorageKey(user?.uid);
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        setPrefs({ ...DEFAULT_PREFS, ...parsed });
      } else {
        setPrefs(DEFAULT_PREFS);
      }
    } catch {
      setPrefs(DEFAULT_PREFS);
    }
  }, [user?.uid]);

  const setPref = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      setPrefs((prev) => {
        const next = { ...prev, [key]: value };
        try {
          localStorage.setItem(getStorageKey(user?.uid), JSON.stringify(next));
        } catch {
          // localStorage full or unavailable
        }
        return next;
      });
    },
    [user?.uid],
  );

  const resetPrefs = useCallback(() => {
    setPrefs(DEFAULT_PREFS);
    try {
      localStorage.removeItem(getStorageKey(user?.uid));
    } catch {
      // ignore
    }
  }, [user?.uid]);

  return (
    <PreferencesContext.Provider value={{ prefs, setPref, resetPrefs }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences(): PreferencesContextType {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
}
